import {
  createContext,
  createSignal,
  useContext,
  onCleanup,
  For,
  type JSX,
} from "solid-js";
import { Portal } from "solid-js/web";

export type ToastVariant = "success" | "error" | "info" | "warning";

export interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
}

export interface ToastActions {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showInfo: (message: string) => void;
  showWarning: (message: string) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastActions>();

let nextId = 0;
const MAX_TOASTS = 5;
const AUTO_DISMISS_MS = 5000;

const variantStyles: Record<ToastVariant, string> = {
  success: "border-t-signal-green",
  error: "border-t-signal-red",
  info: "border-t-signal-blue",
  warning: "border-t-signal-amber",
};

const variantIcons: Record<ToastVariant, JSX.Element> = {
  success: (
    <svg class="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path
        fill-rule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clip-rule="evenodd"
      />
    </svg>
  ),
  error: (
    <svg class="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path
        fill-rule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
        clip-rule="evenodd"
      />
    </svg>
  ),
  info: (
    <svg class="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path
        fill-rule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clip-rule="evenodd"
      />
    </svg>
  ),
  warning: (
    <svg class="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path
        fill-rule="evenodd"
        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
        clip-rule="evenodd"
      />
    </svg>
  ),
};

function ToastContainer(props: {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  return (
    <Portal>
      <div
        data-toast-container
        class="fixed bottom-4 right-4 flex flex-col gap-2"
        style={{ "z-index": "var(--z-toast)" }}
      >
        <For each={props.toasts}>
          {(toast) => (
            <div
              class={`px-4 py-3 rounded bg-surface text-ink border border-line border-t-2 shadow-overlay flex items-center gap-3 animate-in slide-in-from-bottom-2 duration-300 min-w-[280px] max-w-[420px] ${variantStyles[toast.variant]}`}
            >
              {variantIcons[toast.variant]}
              <span class="text-sm font-medium flex-1">{toast.message}</span>
              <button
                type="button"
                onClick={() => props.onDismiss(toast.id)}
                class="ml-2 text-ink-3 hover:text-ink shrink-0 focus-ring"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}
        </For>
      </div>
    </Portal>
  );
}

export function ToastProvider(props: { children: JSX.Element }) {
  const [toasts, setToasts] = createSignal<ToastItem[]>([]);
  const timers = new Map<number, ReturnType<typeof setTimeout>>();

  const dismiss = (id: number) => {
    const timer = timers.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const show = (message: string, variant: ToastVariant) => {
    const id = ++nextId;
    setToasts((prev) => {
      const next = [...prev, { id, message, variant }];
      if (next.length > MAX_TOASTS) {
        const evicted = next[0];
        const timer = timers.get(evicted.id);
        if (timer) {
          clearTimeout(timer);
          timers.delete(evicted.id);
        }
        return next.slice(-MAX_TOASTS);
      }
      return next;
    });

    // Auto-dismiss for non-error toasts (5s)
    if (variant !== "error") {
      const timer = setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
      timers.set(id, timer);
    }
  };

  onCleanup(() => {
    for (const timer of timers.values()) {
      clearTimeout(timer);
    }
  });

  const actions: ToastActions = {
    showSuccess: (msg) => show(msg, "success"),
    showError: (msg) => show(msg, "error"),
    showInfo: (msg) => show(msg, "info"),
    showWarning: (msg) => show(msg, "warning"),
    dismiss,
  };

  return (
    <ToastContext.Provider value={actions}>
      {props.children}
      <ToastContainer toasts={toasts()} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastActions {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
